/**
 * CityLimitNote — плашка «данные неполные, показан срез по городам».
 *
 * Появляется рядом с ЛЮБОЙ суммой или обзором, которые урезаны ограничением
 * Администратора по городам (см. city_gate_implementation_plan.md).
 *
 * ⚠️ Почему это не украшение. Список без плашки честен сам по себе: не видишь
 * строку — не видишь, и вопросов нет. Урезанная СУММА выглядит ровно как
 * полная, поэтому врёт молча: человек сверяет её с бухгалтерией и решает, что
 * сломана система. Плашка превращает «система глючит» в «это мой срез».
 *
 * ⚠️ И почему она обязана быть ВЕЗДЕ. Как только плашка есть на пяти экранах
 * из семи, пользователь выучивает правило «плашки нет — значит цифра полная»,
 * и два оставшихся экрана начинают врать активно, а не пассивно. Появилось
 * новое число под гейтом — плашка едет туда же, в том же коммите.
 *
 * Текст говорит о неполноте ПЕРВЫМ, а список городов — вторым. «По городам:
 * Вологда, Череповец» сообщает срез, но не сообщает главного: что это не вся
 * организация.
 *
 * @param {{limited:boolean, city_ids?:number[], unassigned?:number}} value
 *        то, что бэкенд отдаёт в поле `city_limit`
 * @param {boolean} inline компактный вид — одной строкой, без рамки
 */
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useCities, cityLabel } from '../hooks/useCities';

export default function CityLimitNote({ value, inline = false }) {
  const { t, i18n } = useTranslation();
  const { cities } = useCities();

  if (!value || !value.limited) return null;

  const ids = value.city_ids || [];
  // Справочник у ограниченного пользователя и так отдаёт только его города
  // (см. GET /properties/cities-ref), но подстраховываемся: если названия ещё
  // не доехали, плашку всё равно показываем — без списка, но с предупреждением.
  const names = cities.filter(c => ids.includes(c.id)).map(c => cityLabel(c, i18n.language));
  const unassigned = Number(value.unassigned) || 0;

  const text = [
    t('cityLimit.partial'),
    names.length ? t('cityLimit.cities', { list: names.join(', ') }) : null,
    // Записи без Объекта не относятся ни к какому городу и в срез не вошли.
    // Молчать про них нельзя: это слепая зона, о которой человек должен знать,
    // чтобы спросить Суперадмина, а не искать ошибку в своих цифрах.
    unassigned ? t('cityLimit.unassigned', { count: unassigned }) : null,
  ].filter(Boolean).join(' · ');

  if (inline) {
    return (
      <span
        style={{ fontSize: 12, color: 'var(--text3)', whiteSpace: 'normal' }}
        data-ph-capture-attribute-city-limited="1"
      >
        {text}
      </span>
    );
  }

  return (
    <div
      style={{
        display: 'flex', alignItems: 'flex-start', gap: 8,
        padding: '8px 12px', marginBottom: 12,
        background: 'var(--bg2)', border: '1px solid var(--border)',
        borderRadius: 8, fontSize: 12, color: 'var(--text2)', lineHeight: 1.45,
      }}
      data-ph-capture-attribute-city-limited="1"
    >
      <span aria-hidden style={{ flexShrink: 0 }}>ⓘ</span>
      <span>{text}</span>
    </div>
  );
}
